import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Tag, Check, X, Loader2, Percent, DollarSign } from 'lucide-react';

const PromoCodeInput = ({ plan, onApply, onRemove, appliedDiscount }) => {
  const { api } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleValidate = async () => {
    if (!code.trim()) {
      setError('Please enter a promo code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/promo-codes/validate?code=${code.toUpperCase()}&plan=${plan}`);
      if (response.data.valid) {
        const discount = response.data.discount;
        onApply({
          code: discount.code,
          discount_type: discount.discount_type,
          discount_value: discount.discount_value,
          description: discount.description,
          stripe_coupon_id: discount.stripe_coupon_id
        });
        toast.success(`Promo code "${discount.code}" applied!`);
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid promo code';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setCode('');
    setError(null);
    onRemove();
    toast.info('Promo code removed');
  };

  if (appliedDiscount) {
    return (
      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-800 rounded-full flex items-center justify-center">
              <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                <Tag className="w-4 h-4" />
                {appliedDiscount.code}
              </p>
              <p className="text-sm text-emerald-600 dark:text-emerald-400">
                {appliedDiscount.discount_type === 'percentage' ? (
                  <span className="flex items-center gap-1">
                    <Percent className="w-3 h-3" />
                    {appliedDiscount.discount_value}% off
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    ${appliedDiscount.discount_value} off
                  </span>
                )}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Enter promo code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleValidate()}
            className="pl-10 uppercase"
            data-testid="promo-code-input"
          />
        </div>
        <Button
          onClick={handleValidate}
          disabled={loading || !code.trim()}
          variant="outline"
          data-testid="apply-promo-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Apply'
          )}
        </Button>
      </div>
      {error && (
        <p className="text-sm text-red-500 flex items-center gap-1">
          <X className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
};

export default PromoCodeInput;
